import React from 'react'
import moment from 'moment'
import { Alert, Box, Button, LinearProgress, Paper, Stack, TextField, Typography } from '@mui/material'
import useSWR from 'swr'
import api from 'utils/api'
import { browserTimeZone } from 'utils/time'

export const defaultPeriod = () => ({ from: moment().subtract(29, 'days').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') })
export const periodQuery = ({ from, to }) => {
  const start = moment(from, 'YYYY-MM-DD', true)
  const end = moment(to, 'YYYY-MM-DD', true)
  if (!start.isValid() || !end.isValid() || start.isAfter(end)) return null
  return { startDate: start.startOf('day').toISOString(), endDate: end.endOf('day').toISOString() }
}
const fetchReport = async ([, url]) => ({ data: await api.get(url), updatedAt: new Date() })
export function useReport(endpoint, params, enabled = true) {
  const key = enabled && params ? ['report', `${endpoint}?${new URLSearchParams(params)}`] : null
  const { data: result, error, isLoading, isValidating, mutate } = useSWR(key, fetchReport, {
    shouldRetryOnError: false, revalidateOnFocus: false, revalidateOnMount: true, keepPreviousData: false,
  })
  return {
    data: key && !error ? result?.data : undefined,
    error,
    loading: Boolean(key) && (isLoading || (!result && isValidating)),
    refreshing: isValidating,
    updatedAt: key ? result?.updatedAt : null,
    retry: mutate,
  }
}
export function PeriodFilter({ period, onChange, children }) {
  const valid = periodQuery(period)
  return <Paper variant="outlined" sx={{ p: 2.5 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
      <TextField size="small" type="date" label="From date" InputLabelProps={{ shrink: true }} value={period.from} onChange={event => onChange({ ...period, from: event.target.value })} error={!valid} />
      <TextField size="small" type="date" label="Through date" InputLabelProps={{ shrink: true }} value={period.to} onChange={event => onChange({ ...period, to: event.target.value })} error={!valid} />
      {children}
    </Stack>
    <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>Dates and times use your browser time zone ({browserTimeZone()}). Both selected dates are included.</Typography>
    {!valid && <Alert severity="warning" sx={{ mt: 2 }}>Enter valid dates with the start on or before the end.</Alert>}
  </Paper>
}
export function ReportState({ report, label }) {
  if (report.error) return <Alert severity="error" action={<Button color="inherit" onClick={() => report.retry()}>Retry</Button>}>{label} could not be loaded. Try again.</Alert>
  return report.loading ? <LinearProgress aria-label={`Loading ${label.toLowerCase()}`} /> : null
}
export function Metric({ label, value, note, sx }) {
  return <Paper variant="outlined" sx={{ p: 2.5, minWidth: 0, ...sx }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography component="div" variant="h5" fontWeight={600} mt={0.75} sx={{ overflowWrap: 'anywhere', fontVariantNumeric: 'tabular-nums' }}>{value ?? '—'}</Typography>
    {note && <Typography variant="caption" color="text.secondary" display="block" mt={1}>{note}</Typography>}
  </Paper>
}
export function MetricGrid({ children, sx }) {
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 2, ...sx }}>{children}</Box>
}
export const money = value => value == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value))

export function ReportRefresh({ reports }) {
  const refreshing = reports.some(report => report.refreshing || report.loading)
  const complete = reports.every(report => report.data && !report.error && report.updatedAt)
  const updatedAt = complete ? new Date(Math.min(...reports.map(report => report.updatedAt.getTime()))) : null
  return <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
    <Typography variant="caption" color="text.secondary" role="status">
      {refreshing ? 'Updating…' : updatedAt ? `Updated at ${updatedAt.toLocaleString('en-US')}` : 'Update time unavailable'}
    </Typography>
    <Button size="small" disabled={refreshing} onClick={() => Promise.allSettled(reports.map(report => report.retry()))}>Refresh</Button>
  </Stack>
}
